import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { clubSidebar } from '@/config/sidebarConfig.ts';
import { deleteTeamApi, getTeamsApi } from '@/lib/api/teamsApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { PAY_STATUS_CONFIG, REG_STATUS_CONFIG } from '@/lib/status-config';
import { getTeamSportingCaptain } from '@/lib/team-sporting-captain';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Team } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Edit,
  Plus,
  Search,
  Swords,
  Trash2,
  Trophy,
  Users,
  Users2,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import ConfirmModal from '../../components/ui/ConfirmModal';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';
import CreateTeamModal from './components/create-team-modal';
import EditTeamModal from './components/edit-team-modal';
import TeamDetailModal from './components/team-detail-modal';

// ─── Constants ───────────────────────────────────────────────────

const PAGE_SIZE = 12;

// ─── Badge configs ────────────────────────────────────────────────

// ─── Helpers ─────────────────────────────────────────────────────

// ─── Main Page ───────────────────────────────────────────────────

export default function ClubTeams() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId;
  const queryClient = useQueryClient();

  // ─── URL params ───
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const regStatus = (searchParams.get('regStatus') ?? '') as TeamRegistrationStatus | '';
  const debouncedSearch = searchParams.get('search') ?? '';

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const [searchInput, setSearchInput] = useState(debouncedSearch);

  // ─── Modals ───
  const [openCreate, setOpenCreate] = useState(false);
  const [editTeam, setEditTeam] = useState<Team | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Team | null>(null);
  const [detailTeam, setDetailTeam] = useState<Team | null>(null);

  // ─── Stats query (all teams for club) ───
  const { data: allTeamsData } = useQuery({
    queryKey: queryKeys.teams.list({ clubId, limit: 100 }),
    queryFn: () => getTeamsApi({ clubId: clubId!, limit: 100 }),
    enabled: !!clubId && isAuthenticated,
  });
  const allTeams: Team[] = useMemo(() => allTeamsData?.data ?? [], [allTeamsData]);

  // ─── List query (paginated + filters) ───
  const listParams = {
    clubId: clubId!,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(regStatus && { registrationStatus: regStatus }),
    page,
    limit: PAGE_SIZE,
  };

  const {
    data: listData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.teams.list(listParams as Record<string, unknown>),
    queryFn: () => getTeamsApi(listParams),
    enabled: !!clubId && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const teams: Team[] = listData?.data ?? [];
  const total = listData?.meta?.total ?? 0;
  const totalPages = listData?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // ─── Stats ───
  const totalTeams = allTeamsData?.meta?.total ?? 0;
  const confirmedCount = useMemo(
    () => allTeams.filter(t => t.registrationStatus === TeamRegistrationStatus.CONFIRMED).length,
    [allTeams],
  );
  const pendingCount = useMemo(
    () => allTeams.filter(t => t.registrationStatus === TeamRegistrationStatus.PENDING).length,
    [allTeams],
  );
  const paidCount = useMemo(() => allTeams.filter(t => t.paymentStatus === PaymentStatus.COMPLETED).length, [allTeams]);

  // ─── Delete mutation ───
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTeamApi(id),
    onSuccess: () => {
      toast.success('Équipe supprimée');
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all() });
    },
    onError: () => {
      toast.error('Erreur lors de la suppression');
    },
  });

  function invalidateAndClose() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all() });
    setOpenCreate(false);
    setEditTeam(null);
  }

  // ─── Search debounce → URL ───
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Équipes'
          subtitle='Gérez les équipes engagées en compétition'
          actions={
            clubId ? (
              <button
                onClick={() => setOpenCreate(true)}
                className='flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm shadow-emerald-200'
              >
                <Plus className='w-4 h-4' />
                <span className='text-sm font-bold'>Nouvelle équipe</span>
              </button>
            ) : undefined
          }
        />

        {/* ─── Stats ── */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <StatCard
            icon={Users2}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Total équipes'
            value={String(totalTeams || '—')}
            subtitle='Toutes les équipes du club'
          />
          <StatCard
            icon={CheckCircle}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Confirmées'
            value={String(confirmedCount)}
            subtitle='Inscriptions validées'
          />
          <StatCard
            icon={Swords}
            iconBg='bg-orange-100'
            iconColor='text-orange-600'
            label='En attente'
            value={String(pendingCount)}
            subtitle='En cours de traitement'
          />
          <StatCard
            icon={Trophy}
            iconBg='bg-purple-100'
            iconColor='text-purple-600'
            label='Paiements reçus'
            value={String(paidCount)}
            subtitle='Inscriptions payées'
          />
        </div>

        {/* ─── Filters ── */}
        <div className='flex items-center gap-3 mb-6 flex-wrap'>
          <div className='relative flex-1 min-w-[200px] max-w-xs'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder='Rechercher une équipe…'
              className='w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm'
            />
          </div>
          <select
            value={regStatus}
            onChange={e => {
              setSearchParams(
                prev => {
                  const next = new URLSearchParams(prev);
                  if (e.target.value) next.set('regStatus', e.target.value);
                  else next.delete('regStatus');
                  next.set('page', '1');
                  return next;
                },
                { replace: true },
              );
            }}
            className='px-3 py-2.5 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm text-slate-700 bg-white'
          >
            <option value=''>Tous les statuts</option>
            {Object.entries(REG_STATUS_CONFIG).map(([value, cfg]) => (
              <option key={value} value={value}>
                {cfg.label}
              </option>
            ))}
          </select>
          {isFetching && !isLoading && <span className='text-xs text-slate-400'>Actualisation…</span>}
        </div>

        {/* ─── Team grid ── */}
        {isLoading ? (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className='h-52 animate-pulse rounded-2xl bg-slate-100' />
            ))}
          </div>
        ) : teams.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-20 text-slate-400 gap-3'>
            <Users2 className='w-12 h-12 opacity-30' />
            <p className='text-sm font-medium'>
              Aucune équipe{debouncedSearch || regStatus ? ' avec ces filtres' : ' pour ce club'}
            </p>
            {!debouncedSearch && !regStatus && clubId && (
              <button
                onClick={() => setOpenCreate(true)}
                className='mt-2 flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors'
              >
                <Plus className='w-4 h-4' />
                Créer la première équipe
              </button>
            )}
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {teams.map(team => {
              const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
              const payCfg = PAY_STATUS_CONFIG[team.paymentStatus];
              const playerCount = team.players?.length ?? 0;

              return (
                <div
                  key={team.id}
                  className='bg-white rounded-2xl p-5 border border-slate-200 hover:shadow-lg hover:border-emerald-200 transition-all group'
                >
                  {/* Header */}
                  <div className='flex items-start justify-between mb-3'>
                    <div className='min-w-0 flex-1'>
                      <button
                        onClick={() => setDetailTeam(team)}
                        className='font-bold text-slate-900 text-base hover:text-emerald-700 transition-colors text-left truncate block max-w-full'
                      >
                        {team.name}
                      </button>
                      {team.competition && (
                        <p className='text-xs text-slate-400 mt-0.5 truncate'>
                          {team.competition.name} · {formatDateAbbreviatedFr(team.competition.date)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Status badges */}
                  <div className='flex flex-wrap gap-1.5 mb-4'>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${regCfg.bg} ${regCfg.text}`}
                    >
                      {regCfg.label}
                    </span>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${payCfg.bg} ${payCfg.text}`}
                    >
                      {payCfg.label}
                    </span>
                  </div>

                  {/* Players avatars */}
                  {team.players && team.players.length > 0 && (
                    <div className='flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-xl'>
                      <div className='flex -space-x-2'>
                        {team.players.slice(0, 4).map(p => (
                          <div
                            key={p.id}
                            className='w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 border-2 border-white'
                            title={`${p.firstName} ${p.lastName}`}
                          >
                            {p.firstName.charAt(0)}
                            {p.lastName.charAt(0)}
                          </div>
                        ))}
                        {team.players.length > 4 && (
                          <div className='w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 border-2 border-white'>
                            +{team.players.length - 4}
                          </div>
                        )}
                      </div>
                      <div className='flex items-center gap-1 text-sm text-slate-600'>
                        <Users className='w-3.5 h-3.5 text-slate-400' />
                        <span className='font-semibold'>{playerCount}</span> joueur{playerCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  )}

                  {/* Captain */}
                  {getTeamSportingCaptain(team) && (
                    <p className='text-xs text-slate-500 mb-4 truncate'>
                      Cap. {getTeamSportingCaptain(team)?.firstName} {getTeamSportingCaptain(team)?.lastName}
                    </p>
                  )}

                  {/* Actions */}
                  <div className='flex items-center gap-2 pt-3 border-t border-slate-100'>
                    <button
                      onClick={() => setDetailTeam(team)}
                      className='flex-1 text-center text-xs font-semibold text-slate-600 hover:text-emerald-700 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors'
                    >
                      Détails
                    </button>
                    <button
                      onClick={() => setEditTeam(team)}
                      title='Modifier le nom'
                      aria-label='Modifier le nom'
                      className='p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors'
                    >
                      <Edit className='w-4 h-4' />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(team)}
                      title="Supprimer l'équipe"
                      aria-label="Supprimer l'équipe"
                      className='p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                    >
                      <Trash2 className='w-4 h-4' />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── Pagination ── */}
        {total > PAGE_SIZE && (
          <div className='flex items-center justify-between mt-8 pt-6 border-t border-slate-200'>
            <p className='text-sm text-slate-500'>
              Affichage de <span className='font-semibold text-slate-700'>{from}</span> à{' '}
              <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
              <span className='font-semibold text-slate-700'>{total.toLocaleString('fr-FR')}</span> équipes
            </p>
            {totalPages > 1 && (
              <div className='flex items-center gap-1'>
                <button
                  onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                  disabled={page === 1}
                  className='rounded-lg border border-slate-200 p-2 hover:bg-slate-50 disabled:opacity-40 transition-colors'
                  aria-label='Page précédente'
                >
                  <ChevronLeft className='h-4 w-4' />
                </button>
                {getPageNumbers(page, totalPages).map((p, i) =>
                  p === '...' ? (
                    <span key={`dots-${i}`} className='px-2 text-slate-400'>
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setParam('page', String(p))}
                      aria-current={p === page ? 'page' : undefined}
                      className={`rounded-lg px-3 py-1 text-sm font-semibold transition-colors ${
                        p === page ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
                  disabled={page >= totalPages}
                  className='rounded-lg border border-slate-200 p-2 hover:bg-slate-50 disabled:opacity-40 transition-colors'
                  aria-label='Page suivante'
                >
                  <ChevronRight className='h-4 w-4' />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Modals ── */}
      {openCreate && clubId && (
        <CreateTeamModal clubId={clubId} onClose={() => setOpenCreate(false)} onSuccess={invalidateAndClose} />
      )}
      {editTeam && <EditTeamModal team={editTeam} onClose={() => setEditTeam(null)} onSuccess={invalidateAndClose} />}
      <TeamDetailModal
        team={detailTeam}
        onClose={() => setDetailTeam(null)}
        onEdit={t => {
          setDetailTeam(null);
          setEditTeam(t);
        }}
        onDelete={t => {
          setDetailTeam(null);
          setConfirmDelete(t);
        }}
      />
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Supprimer l'équipe"
        message={`Voulez-vous vraiment supprimer l'équipe "${confirmDelete?.name}" ? Cette action est irréversible et retirera l'équipe du concours.`}
        confirmLabel='Supprimer'
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
        isLoading={deleteMutation.isPending}
      />
    </DashboardLayout>
  );
}
